export interface PriceInput {
  price: number;
  date: string | Date;
  listPrice?: number | null;
}

/**
 * Client-side shape of the server's ItemStats.
 * Date fields are typed `string | Date | null` because Eden Treaty infers the
 * server's `Date | null` directly (no JSON coercion in the type system), while
 * at runtime values arrive as JSON strings. Consumers should wrap with
 * `new Date(value)`, which accepts both.
 * Keep this interface in sync with `apps/server/src/lib/item-stats.ts`.
 */
export interface ItemStats {
  current: number | null;
  min: number;
  max: number;
  meanPrice: number;
  volatility: number;
  variance: number;
  stdDev: number;
  isLowestPrice: boolean;
  isPriceDrop: boolean;
  previousPrice: number | null;
  samePriceOccurrencesInLastWeek: number;
  currentAt: string | Date | null;
  previousAt: string | Date | null;
  minAt: string | Date | null;
  maxAt: string | Date | null;
  startDate: string | Date | null;
  lastInStockPrice: number | null;
  lastInStockAt: string | Date | null;
}

export interface RichStats {
  current: { price: number; date: string | Date };
  min: { price: number; date: string | Date };
  max: { price: number; date: string | Date };
  startDate: string | Date;
}

export function computeRichStats(prices: PriceInput[]): RichStats {
  const withPrice = prices.filter((p) => p.price != null && p.price > 0);

  if (withPrice.length === 0) {
    const now = new Date().toISOString();
    return {
      current: { price: 0, date: now },
      min: { price: 0, date: now },
      max: { price: 0, date: now },
      startDate: prices[0]?.date ?? now,
    };
  }

  const currentPoint = withPrice[withPrice.length - 1];
  const current = { price: currentPoint.price, date: currentPoint.date };

  let minPoint = withPrice[0];
  let maxPoint = withPrice[0];
  for (const p of withPrice) {
    if (p.price < minPoint.price) minPoint = p;
    if (p.price > maxPoint.price) maxPoint = p;
  }

  return {
    current,
    min: { price: minPoint.price, date: minPoint.date },
    max: { price: maxPoint.price, date: maxPoint.date },
    startDate: prices[0].date,
  };
}

export interface PriceData {
  date: string;
  price: number | null;
  listPrice?: number;
}

/** Raw price entry as returned by the API — price may be null during out-of-stock periods. */
export interface RawPriceEntry {
  date: string | Date;
  price: number | null;
  listPrice?: number | null;
}

/**
 * Maps raw API price entries to the shape expected by the Chart component.
 * Null prices (out-of-stock periods) are preserved intentionally — the Chart
 * component renders them as dashed gap segments.
 */
export function mapPricesForChart(prices: RawPriceEntry[]): PriceData[] {
  return prices.map((p) => ({
    date: String(p.date),
    price: p.price,
    listPrice: p.listPrice ?? undefined,
  }));
}
